<script lang="ts">
  import { canSwitchModes } from '../../../voice/types';
  import VoiceSwitch from '../../../voice/voice-switch.svelte';
  /** store*/
  import { onDestroy, onMount } from 'svelte';
  import Device from 'svelte-device-info';
  import { fade } from 'svelte/transition';

  import {
    state_apiErrors,
    state_busyApi,
    state_conversationResults,
    state_fileAttachmentErrorKey,
    state_questionImageSearch,
    state_scrollingBehavior,
    state_searchResult,
    state_searchString,
    state_searchSuggestion,
    state_selectedIndicesList,
  } from '../../../lib/store/state';
  import { ApiTypes } from '../../../lib/enums/api.enum';
  import { WindowsCustomEventTypes } from '../../../lib/enums/windows-custom-event.enum';
  import { WidgetType } from '../../../lib/enums/widget-type.enum';
  import { ConfigStore } from '../../../lib/store/config';
  import { isIpad } from '../../../lib/helpers/detect-os';
  import type { IRequestSearchSuggestion } from '../../../lib/interfaces/request-search-suggestion.interface';
  import type { IRequestSearch } from '../../../lib/interfaces/request-search-question.interface';
  import { IconThemeType } from '../../../lib/enums/themes/icon-theme-type.enum';
  import SuggestionComponent from '../search-input-suggestion.svelte';
  import FileAttachmentDisplay from '../file-attachment-display.svelte';
  import InputSpeechRecognitionComponent from '../search-input-speech-recognition.svelte';
  import ModeSwitch from '../mode-switch.svelte';
  import { _ } from 'svelte-i18n';
  import { limit } from '../../../lib/helpers/limit';
  import { purifyString } from '../../../lib/helpers/purify-string';
  import SvgIcon from '../svg-icon.svelte';
  import {
    getAddFilesConfig,
    getEnabledFeatures,
  } from '../../../lib/helpers/features';

  let txtSearch: any;
  let searchSuggestionList: HTMLElement;
  let suggestionTimer: any;

  let isMinStringMsg: boolean;
  let isAutoSuggestionUpNow: boolean;
  let focusedIndex: number = -1;

  $: isSearchBusy = $state_busyApi.includes(ApiTypes.postQuestion);

  $: isAutoSuggestionUp();

  let lastAnnouncedSuggestionCount = 0;

  $: visibleSuggestionCount =
    $state_searchSuggestion?.length > 1 ||
    ($state_searchSuggestion?.length === 1 &&
      $state_searchSuggestion[0].data.question !== $state_searchString)
      ? $state_searchSuggestion.length
      : 0;

  $: announceSuggestionCount(visibleSuggestionCount);
  $: hasBlockingAttachment = $state_questionImageSearch.some(
    (attachment) =>
      attachment.status === 'uploading' || attachment.status === 'error'
  );

  const announceSuggestionCount = (count: number) => {
    if (count <= 0 || count === lastAnnouncedSuggestionCount) {
      // Suggestions just went away — blank the live region so the last
      // "N suggestions available" isn't left for the screen reader.
      if (count <= 0 && lastAnnouncedSuggestionCount > 0) {
        window.dispatchEvent(
          new CustomEvent(WindowsCustomEventTypes.announceToScreenReader, {
            detail: { clear: true },
          })
        );
      }
      lastAnnouncedSuggestionCount = count;
      return;
    }
    lastAnnouncedSuggestionCount = count;
    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.announceToScreenReader, {
        detail: {
          message: $_('search_input.suggestions_available', {
            values: { count },
          }),
        },
      })
    );
  };

  state_apiErrors.subscribe((x) => {
    const postQuestionError = x.find((x) =>
      [ApiTypes.postQuestion, ApiTypes.streamPOSTQuestion].includes(x.apiType)
    );

    if (postQuestionError?.errorType == 'AbortError') return;

    if (postQuestionError && $state_conversationResults.length) {
      state_searchString.set(
        $state_conversationResults[$state_conversationResults.length - 1]
          .question
      );
      state_scrollingBehavior.set(!$state_scrollingBehavior);
    }
  });

  const speechChange = (e: any) => {
    state_searchString.set(e.detail);
    state_scrollingBehavior.set(!$state_scrollingBehavior);
    search();
  };

  window.addEventListener(
    WindowsCustomEventTypes.speechSearchRequest,
    speechChange
  );

  const canProgrammaticallyFocus = () => {
    return ![WidgetType.chat, WidgetType.cip, WidgetType.ce].includes(
      $ConfigStore.widgetType
    );
  };

  const focusSearchInput = () => {
    if (!canProgrammaticallyFocus()) return;

    txtSearch?.focus();
  };

  window.addEventListener(
    WindowsCustomEventTypes.focusSearchInput,
    focusSearchInput
  );

  onMount(() => {
    if (canProgrammaticallyFocus()) {
      if (!Device.isPhone && !isIpad()) {
        txtSearch?.focus();
      }
    }

    searchOnLoadWithUrlParam();

    // shadowDomUpdates("clever-search-input", true);
  });

  const searchOnLoadWithUrlParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const question = urlParams.get('gqa_q');

    if (!!question?.length)
      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.requestSearch, {
          detail: {
            query: question,
            flags: $ConfigStore.customAttributes?.flags,
            cookies: $ConfigStore.customAttributes?.cookies,
            url: $ConfigStore.customAttributes?.url,
            requestType: 'turnX',
          },
        })
      );
  };

  /** method to trigger search request action*/
  const search = () => {
    if (!$state_searchString?.length) return;

    if (
      $ConfigStore.isTextAreaSearch &&
      !purifyString($state_searchString ?? '').length
    ) {
      return;
    }

    if ($state_searchString.length > $ConfigStore.searchStringMaxLength) {
      return;
    }

    if (hasBlockingAttachment) {
      const hasUploading = $state_questionImageSearch.some(
        (attachment) => attachment.status === 'uploading'
      );
      state_fileAttachmentErrorKey.set(
        hasUploading
          ? 'search_input.error_file_pending'
          : 'search_input.error_file_upload'
      );
      return;
    }

    /** current result search and requesting search string is same*/
    if (
      $state_searchString === $state_searchResult?.question &&
      [
        WidgetType.chat,
        WidgetType.cb,
        WidgetType.cip,
        WidgetType.ce,
        WidgetType.cmt,
        WidgetType.ctt,
      ].includes($ConfigStore.widgetType)
    )
      return;

    if ($ConfigStore.searchStringMinLength > $state_searchString?.length) {
      isMinStringMsg = true;
      return;
    }

    isMinStringMsg = false;

    const data: IRequestSearch = {
      query: purifyString($state_searchString),
      flags: $ConfigStore.customAttributes?.flags,
      cookies: $ConfigStore.customAttributes?.cookies,
      url: $ConfigStore.customAttributes?.url,
      requestType: 'turnX',
    };

    const readyAttachmentIds = $state_questionImageSearch
      .filter((attachment) => attachment.status === 'ready' && attachment.id)
      .map((attachment) => attachment.id!);
    if (readyAttachmentIds.length) {
      data.files = readyAttachmentIds;
    }

    if ($state_selectedIndicesList.length) {
      data.index = $state_selectedIndicesList.toString().replace(', ', '');
    }

    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.requestSearch, {
        detail: data,
      })
    );

    if (
      ![
        WidgetType.chat,
        WidgetType.cb,
        WidgetType.cip,
        WidgetType.ce,
        WidgetType.cmt,
        WidgetType.ctt,
      ].includes($ConfigStore.widgetType) &&
      data.query
    )
      state_searchString.set(data.query);

       state_questionImageSearch.set([]);
  };

  const handleOnKeyUp = (e: any) => {
    const shouldHandleSuggestions =
      $ConfigStore.searchSuggestion &&
      $state_searchString?.length >= $ConfigStore.searchSuggestionMinLength;

    switch (e.key) {
      case 'Enter':
        return;
      case 'ArrowDown':
        e.preventDefault();
        break;
      case 'ArrowUp':
        e.preventDefault();
        break;
      default:
        focusedIndex = -1;
        break;
    }

    if (!shouldHandleSuggestions) {
      clearTimeout(suggestionTimer);
      state_searchSuggestion.set([]);
      return;
    }

    if (shouldHandleSuggestions) {
      getSuggestions();
    }
  };

  const getSuggestions = (): void => {
    clearTimeout(suggestionTimer);
    suggestionTimer = setTimeout(() => {
      const data: IRequestSearchSuggestion = {
        query: purifyString($state_searchString),
        maxHits: 5,
      };

      if ($state_selectedIndicesList.length)
        data.index = $state_selectedIndicesList.toString().replace(', ', '');

      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.requestSuggestion, {
          detail: data,
        })
      );
    }, 500);
  };

  const isAutoSuggestionUp = () => {
    try {
      const isIPhone = /iPhone|iPod/i.test(navigator.userAgent);
      const height = isIPhone
        ? window.visualViewport?.height || window.innerHeight
        : window.innerHeight;

      const wInnerHeight = height / 2;

      const elTop = txtSearch.getBoundingClientRect().top;

      if (elTop === 0) {
        isAutoSuggestionUpNow = false;
        return false;
      } else {
        isAutoSuggestionUpNow = true;
        return true;
      }
    } catch {
      isAutoSuggestionUpNow = false;
      return false;
    }
  };

  onDestroy(() => {
    window.removeEventListener(
      WindowsCustomEventTypes.speechSearchRequest,
      speechChange
    );
    window.removeEventListener(
      WindowsCustomEventTypes.focusSearchInput,
      focusSearchInput
    );
  });

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (searchSuggestionList?.checkVisibility()) return;
      if (Device.isPhone && $ConfigStore.scrollBehavior) {
        txtSearch.blur();
      }

      search();
    }
  };

  const checkCharacter = () => {
    if ($state_searchString?.trim() === '<br>') {
      state_searchString?.set('');
    }
  };
</script>

<div
  class="cs-input-wrapper"
  class:cs-mic-on={$ConfigStore.speechInputOn}
  class:cs-voice-on={canSwitchModes($ConfigStore.widget?.live)}
  class:cs-has-mode-switch={$ConfigStore.modeSwitchOptions?.length}
  class:cs-has-multiple-options={($ConfigStore.canClearChatHistory &&
    getEnabledFeatures($ConfigStore).length > 0) ||
    getEnabledFeatures($ConfigStore).length > 1}
  class:cs-active-hints={$state_questionImageSearch?.length > 0 ||
    $state_searchSuggestion?.length > 1 ||
    ($state_searchSuggestion.length == 1 &&
      $state_searchSuggestion[0].data.question != $state_searchString)}
  class:cs-auto-suggestion-up={$state_questionImageSearch?.length > 0 ||
    isAutoSuggestionUpNow}
  class:cs-has-attachment={$state_questionImageSearch?.length > 0}
>
  <!-- <input
    bind:this={txtSearch}
    type="text"
    role="textbox"
    class="cs-search-input"
    class:cs-search-image={$state_questionImageSearch?.length > 0}
    placeholder={$_('search_input.placeholder')}
    aria-label={$_('search_input.placeholder')}
    bind:value={$state_searchString}
    maxlength={$ConfigStore.searchStringMaxLength}
    on:keyup={handleOnKeyUp}
  /> -->

  <span
    tabindex="0"
    bind:this={txtSearch}
    contenteditable
    role="textbox"
    class="cs-search-input"
    class:cs-search-image={$state_questionImageSearch?.length > 0}
    placeholder={$ConfigStore.placeholderSearch ||
      $_('search_input.placeholder')}
    aria-label={$_('search_input.placeholder')}
    bind:innerHTML={$state_searchString}
    on:input={checkCharacter}
    on:keyup={handleOnKeyUp}
    on:keydown={handleKeyDown}
    on:blur={() => state_searchSuggestion.set([])}
    use:limit={$ConfigStore.searchStringMaxLength}
  >
  </span>

  {#if $state_searchSuggestion?.length > 1 || ($state_searchSuggestion.length == 1 && $state_searchSuggestion[0].data.question != $state_searchString)}
    <div class="cs-hint-wrapper" bind:this={searchSuggestionList}>
      <SuggestionComponent
        isautosuggestionup={isAutoSuggestionUp()}
        bind:focusedIndex
      />
    </div>
  {/if}

  {#if $state_questionImageSearch?.length > 0}
    <div class="cs-hint-inner-wrapper cs-hint-up-direction cs-file-attachment">
      <FileAttachmentDisplay />
    </div>
  {/if}

  <button
    disabled={hasBlockingAttachment ||
      ($state_questionImageSearch?.length < 1 &&
        ($state_searchString === $state_searchResult?.question ||
          $ConfigStore.searchStringMinLength > $state_searchString?.length ||
          $ConfigStore.searchStringMaxLength < $state_searchString?.length ||
          isSearchBusy))}
    class:cs-has-mode-switch={$ConfigStore.modeSwitchOptions?.length}
    class={`btn-search ${$ConfigStore.customTheme.iconThemeType ? 'btn-search-' + $ConfigStore.customTheme.iconThemeType : ''}`}
    title={$_('search_input.search_now')}
    aria-label={$_('search_input.search_now')}
    on:click={search}
  >
    {#if $ConfigStore.customTheme.iconThemeType === IconThemeType.smoothicon}
      <svg
        width="13"
        height="13"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M11.8214 0.0977331C12.1097 -0.00751778 12.4219 -0.0287134 12.7219 0.0367172C13.0248 0.102803 13.3024 0.254481 13.5216 0.473719C13.7408 0.692957 13.8925 0.970537 13.9586 1.27346C14.024 1.57338 14.0028 1.88566 13.8976 2.17389L10.3236 12.8859C10.2365 13.1493 10.083 13.3867 9.8781 13.5739C9.6738 13.7606 9.42509 13.8917 9.15572 13.9549C8.8863 14.0206 8.60441 14.015 8.33774 13.9388C8.07131 13.8627 7.82926 13.7187 7.63529 13.5209L5.71795 11.6123L3.70389 12.6538C3.54684 12.735 3.35857 12.7272 3.20874 12.6333C3.05892 12.5395 2.96981 12.3734 2.9744 12.1967L3.05697 9.01294L10.1019 3.89553C10.3812 3.69267 10.4432 3.30182 10.2403 3.02255C10.0374 2.74327 9.64659 2.68133 9.36731 2.88419L2.20283 8.0884L0.473125 6.35869C0.285802 6.1715 0.147716 5.94054 0.0716193 5.68688C-0.00393617 5.43503 -0.0162115 5.16847 0.0358379 4.91079C0.0879545 4.62928 0.213796 4.36658 0.400577 4.14951C0.588637 3.93095 0.83165 3.76658 1.1045 3.67339L1.10787 3.67224L11.8214 0.0977331Z"
          fill="currentColor"
        />
      </svg>
    {:else if $ConfigStore.customTheme.iconThemeType === IconThemeType.curveicon}
      <svg
        width="19"
        height="19"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_6_13416)">
          <path
            d="M4 12L5.41 13.41L11 7.83V20H13V7.83L18.58 13.42L20 12L12 4L4 12Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_6_13416">
            <rect width="24" height="24" fill="white" />
          </clipPath>
        </defs>
      </svg>
    {:else}
      <SvgIcon name="send-horizontal" />
    {/if}
  </button>

  {#if $ConfigStore.speechInputOn}
    <InputSpeechRecognitionComponent />
  {/if}
  <VoiceSwitch />

  <!-- <button class="btn-search" alt="Search Now" on:click={search} /> -->
  {#if $ConfigStore.searchStringMaxLength <= $state_searchString?.length}
    <div transition:fade|global class="cs-search-length-hint">
      {$_('search_input.error_question_long')}
    </div>
  {/if}

  {#if isMinStringMsg && $ConfigStore.searchStringMinLength > $state_searchString?.length && !state_questionImageSearch && $state_questionImageSearch && $state_questionImageSearch.length <= 0}
    <div transition:fade|global class="cs-search-length-hint">
      {$_('search_input.error_question_short')}
    </div>
  {/if}
  {#if $state_questionImageSearch && $state_questionImageSearch.length > (getAddFilesConfig($ConfigStore)?.numberOfFiles ?? 1)}
    <div transition:fade|global class="cs-search-length-hint">
      {$_('search_input.error_number_of_files')}
    </div>
  {/if}
  {#if $state_fileAttachmentErrorKey}
    <div transition:fade|global class="cs-search-length-hint">
      {$_($state_fileAttachmentErrorKey)}
    </div>
  {/if}

  <ModeSwitch />
</div>

<style lang="scss">
  @use './../../../scss/components/themes/_search-input-theme';
</style>
